export const name = 'rnPusher';
/**
 * @ngdoc service
 * @name rhino.components.pusher.service:rnPusher
 *
 * @description Rhino Pusher service, providing a simplified facade for working with Pusher.
 * This service uses a single Pusher client configured with the Rhino Registry
 * for easily adding and removing event listeners.  This services automatically
 * subscribes to and unsubscribes from channels as required.  Having a central
 * client also reduces the number of Pusher authorizations.
 *
 * @example
 * ```js
 * rnPusher.bind('devices', 'deviceUpdated', device => {});
 * ```
 */
export default function rnPusher($q, $rootScope, $log, rnBlue) {
  //noinspection BadExpressionStatementJS
  'ngInject';

  let service = {
    bind: bind,
    unbind: unbind,
  };
  return service;

  ////////////////////

  function getBluePusherApi() {
    return rnBlue.getBlueApi().then(blueApi => blueApi.pusher);
  }

  /**
   * @ngdoc function
   * @name bind
   * @methodOf rhino.components.pusher.service:rnPusher
   * @description Binds a callback function to a channel event.
   * @param {string} channelName of the channel to bind to
   * @param {string} eventName of the event to bind to
   * @param {function} callback for pusher to call
   * @param {object} [scope] if provided, rnPusher will ensure the callback is unbound on scope destruction
   * @returns {Promise.<T>|IPromise<TResult>} a promise
   */
  function bind(channelName, eventName, callback, scope) {
    if(!channelName) {
      $log.warn('Attempt to bind without a channel name', channelName, eventName, callback, scope);
      return $q.reject('Unable to bind without a channel name');
    }
    if(!eventName) {
      $log.warn('Attempt to bind without an event name', channelName, eventName, callback, scope);
      return $q.reject('Unable to bind without an event name');
    }
    if(!callback) {
      $log.warn('Attempt to bind without a callback ', channelName, eventName, callback, scope);
      return $q.reject('Unable to bind without a callback');
    }

    return getBluePusherApi().then(bluePusherApi => {
      let decoratedCallback = function pusherCallback(payload) {
        callback(payload);
        // Pusher events are outside of the angular digest loop.
        // Ensure a digest loop here so that any changes made in the callback take effect.
        $rootScope.$digest();
      };

      bluePusherApi.bind(channelName, eventName, decoratedCallback);
      // Gross - save a reference back to the decorated callback.
      // It is this callback that is needed in unbind (see pusher-angular.unbind).
      callback.decorator = decoratedCallback;

      if (scope) {
        scope.$on('$destroy', () => {
          unbind(channelName, eventName, callback);
        });
      }
    });
  }

  /**
   * @ngdoc function
   * @name unbind
   * @methodOf rhino.components.pusher.service:rnPusher
   * @description Unbinds a callback function from a channel event.
   * @param {string} channelName of the channel to unbind from
   * @param {string} eventName of the event to unbind from
   * @param {function} callback to unbind
   * @returns {Promise.<T>|IPromise<TResult>} a promise
   */
  function unbind(channelName, eventName, callback) {
    if(!channelName) {
      $log.warn('Attempt to unbind without a channel name', channelName, eventName, callback);
      return $q.reject('Unable to unbind without a channel name');
    }
    if(!eventName) {
      $log.warn('Attempt to unbind without an event name', channelName, eventName, callback);
      return $q.reject('Unable to unbind without an event name');
    }
    if(!callback) {
      $log.warn('Attempt to unbind without a callback ', channelName, eventName, callback);
      return $q.reject('Unable to unbind without a callback');
    }

    return getBluePusherApi().then(bluePusherApi => {
      let decoratedCallback = callback.decorator;
      bluePusherApi.unbind(channelName, eventName, decoratedCallback);
    });
  }

}
